// Angular
import { NgClass } from '@angular/common';
import {
  Component,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  EventEmitter,
  ViewChild,
  ElementRef,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
// PrimeNG
import { ButtonModule } from 'primeng/button';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SidebarModule } from 'primeng/sidebar';
import { CostCenter } from './models/cost-center.interface';
import { InputTextModule } from 'primeng/inputtext';
import { PanelModule } from 'primeng/panel';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
// Models
import { ControlsOf } from '@shared/models-v3/controls-of.type';
import { ISession } from '@core/models-v2/auth/session.interface';
// Services

import { SessionService } from '@core/services-v2/session/session.service';
import { BlockUiService } from '@core/services-v2/block-ui/block-ui.service';
// Directives
import { ControlErrorsDirective } from '@shared/directives-v3/validation-message/directives/control-errors/control-errors.directive';
import { FormSubmitDirective } from '@shared/directives-v3/validation-message/directives/form-submit/form-submit.directive';
import { CustomerCostCenterApiService } from '@core/services-v2/customer-cost-center/customer-cost-center-api.service';

const NG_MODULES = [ReactiveFormsModule, NgClass];
const PRIME_MODULES = [
  SidebarModule,
  ButtonModule,
  RadioButtonModule,
  InputTextModule,
  PanelModule,
  ToastModule,
];
const DIRECTIVES = [ControlErrorsDirective, FormSubmitDirective];

@Component({
  selector: 'app-cost-center-sidebar',
  standalone: true,
  imports: [NG_MODULES, PRIME_MODULES, DIRECTIVES],
  templateUrl: './cost-center-sidebar.component.html',
  styleUrl: './cost-center-sidebar.component.scss',
  providers: [MessageService],
})
export class CostCenterSidebarComponent implements OnInit {
  //#region ViewChild elements
  @ViewChild('costCenterFormElement')
  costCenterFormElement!: ElementRef<HTMLFormElement>;
  //#endregion

  //#region Inputs & Outputs
  @Input() selectedCostCenterCode: string | null = null;
  @Output() onSubmit: EventEmitter<CostCenter> = new EventEmitter();
  //#endregion

  selectedCostCenterForm!: FormGroup<ControlsOf<{ code: string | null }>>;
  costCenters = signal<CostCenter[]>([]);
  sortedCostCenters = signal<CostCenter[]>([]);
  costCenterForm!: FormGroup<ControlsOf<CostCenter>>;
  visible: boolean = false;
  step = signal<number>(1);
  userSession!: ISession;

  //#region Getters
  get codeField() {
    return this.selectedCostCenterForm.get('code');
  }
  //#endregion

  //#region Dependencies
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly blockUiService = inject(BlockUiService);
  private readonly customerCostCenterService = inject(
    CustomerCostCenterApiService
  );
  private readonly sessionService = inject(SessionService);
  private readonly messageService = inject(MessageService);
  //#endregion

  constructor() {
    this.buildForms();
  }

  ngOnInit(): void {
    this.userSession = this.sessionService.getSession();
    this.fetchCostCenters();
  }

  private buildForms(): void {
    this.selectedCostCenterForm = this.fb.group({
      code: new FormControl<string | null>(null),
    });
    this.costCenterForm = this.fb.group({
      name: ['', Validators.required],
      code: ['', Validators.required],
    });
  }

  /**
   * Obtener los centros de costo del cliente.
   */
  private fetchCostCenters(): void {
    this.customerCostCenterService
      .getAllByDocumentId(this.userSession.documentId)
      .subscribe({
        next: (costCenters) => {
          this.costCenters.set(costCenters);
          this.setSortedCostCenters(this.selectedCostCenterCode);
        },
        error: (error) => {
          console.error(error);
        },
      });
  }

  show(): void {
    this.visible = true;
  }

  onShow(): void {
    this.setSortedCostCenters(this.selectedCostCenterCode);
    this.selectedCostCenterForm.setValue({
      code: this.selectedCostCenterCode || null,
    });
  }

  /**
   * Ordenar los centros de costo.
   * @param selectedCostCenterCode - Código del centro de costo seleccionado.
   * @returns
   */
  private setSortedCostCenters(selectedCostCenterCode: string | null): void {
    const selectedCostCenter = this.costCenters().find(
      (costCenter) => costCenter.code === selectedCostCenterCode
    );
    if (!selectedCostCenter) {
      this.sortedCostCenters.set(this.costCenters());
      return;
    }
    const sortedCostCenters = [
      selectedCostCenter,
      ...this.costCenters().filter(
        (costCenter) => costCenter.code !== selectedCostCenterCode
      ),
    ];
    this.sortedCostCenters.set(sortedCostCenters);
  }

  /**
   * Al ocultar el sidebar.
   */
  onHide(): void {
    this.visible = false;
    this.step.set(1);
    this.sortedCostCenters.set([]);
  }

  /**
   * Al seleccionar centro de costo.
   * @param index - Índice del centro de costo seleccionado.
   */
  onSelectCostCenter(index: number): void {
    const selectedItem = this.sortedCostCenters()[index];
    this.selectedCostCenterForm.setValue({
      code: selectedItem.code,
    });
  }

  /**
   * Guardar el centro de costo seleccionado.
   */
  onSelectCostCenterSubmit(): void {
    this.visible = false;
    const selectedCostCenterCode = this.codeField?.value;
    if (!selectedCostCenterCode) return;
    const selectedCostCenter = this.sortedCostCenters().find(
      (costCenter) => costCenter.code === selectedCostCenterCode
    );
    this.onSubmit.emit(selectedCostCenter);
  }

  onCreateCostCenterSubmit(): void {
    this.costCenterFormElement.nativeElement.dispatchEvent(
      new Event('submit', { cancelable: true })
    );
    if (!this.costCenterForm.valid) return;
    const costCenter = this.costCenterForm.getRawValue();
    this.createCostCenter(costCenter);
  }

  createCostCenter(costCenter: CostCenter): void {
    this.blockUiService.set(true);
    this.customerCostCenterService
      .create(costCenter, this.userSession.documentId)
      .subscribe({
        next: (costCenter) => {
          this.costCenters.set([...this.costCenters(), costCenter]);
          this.sortedCostCenters.set([...this.costCenters()]);
          this.costCenterForm.reset();
          this.step.set(1);
          this.blockUiService.set(false);
          this.messageService.add({
            severity: 'success',
            summary: 'Centro de costo creado',
            detail: 'El centro de costo ha sido creado correctamente.',
          });
        },
        error: () => {
          this.blockUiService.set(false);
          this.messageService.add({
            severity: 'error',
            summary: 'Error creando centro de costo',
            detail: 'Ha ocurrido un error creando el centro de costo.',
          });
        },
      });
  }

  /**
   * Dispara el evento submit del formulario de dirección.
   */
  onSubmitCostCenterForm(): void {
    if (!this.costCenterForm.valid) this.costCenterForm.markAllAsTouched();
  }
}
